import { SubTitleWithImage } from "@stories/atoms/SubTitle/SubTitle";
import * as RouteHelper from "@utils/routes";
import { PageBreadcrumbs } from "@stories/atoms/PageBreadcrumbs/PageBreadcrumbs";
import { useProjectContext } from "@hooks/context/useProjectContext";
import { DirectoryCard } from "@stories/atoms/DirectoryCard/DirectoryCard";
import { useSocialBusiness } from "@hooks/crud/useSocialBusiness";
import { Loader } from "@stories/atoms/Loaders/Loader/Loader";
import Typography from "@mui/material/Typography";
import { Box, Grid, MenuItem, Table, TableCell, TableRow } from "@mui/material";
import { StyledCard } from "@stories/atoms/StyledCard/StyledCard";
import { StyledTextField } from "@stories/atoms/StyledTextField/StyledTextField";
import { useState } from "react";
import {
  SocialCommitmentTypeHour,
  SocialCommitmentTypeSpend,
  socialCommitmentTypeToString,
} from "@interfaces/commitment";

type SocialBusinessSearchCategory =
  | SocialCommitmentTypeSpend
  | SocialCommitmentTypeHour
  | "ALL";

interface SocialBusinessSearchOption {
  value: SocialBusinessSearchCategory;
  label: string;
}

export const Companies = () => {
  const [selectedTargetType, setSelectedTargetType] =
    useState<SocialBusinessSearchCategory>();

  const { selectedProject, selectedProjectOrganisation } = useProjectContext();
  const { socialBusinesses, isSocialBusinessLoading } = useSocialBusiness(
    selectedTargetType !== "ALL" ? selectedTargetType : undefined
  );

  const breadcrumbs = [
    {
      path: RouteHelper.directory(selectedProject.id),
      name: "Smart Directory",
    },
  ];

  const socialTargets = [
    {
      value: "ALL",
      label: "All",
    },
  ] as SocialBusinessSearchOption[];

  Object.values(SocialCommitmentTypeSpend).forEach((s) => {
    if (s != SocialCommitmentTypeSpend.DefaultSpend) {
      socialTargets.push({
        value: s,
        label: socialCommitmentTypeToString(s),
      });
    }
  });
  Object.values(SocialCommitmentTypeHour).forEach((s) => {
    if (s != SocialCommitmentTypeHour.DefaultHour) {
      socialTargets.push({
        value: s,
        label: socialCommitmentTypeToString(s),
      });
    }
  });

  return (
    <>
      <StyledCard>
        <Grid container spacing={2} sx={{ padding: "12px", display: "flex" }}>
          <Grid
            item
            xs={12}
            md={10}
            sx={{ display: "flex", alignContent: "center" }}
          >
            <SubTitleWithImage
              title={`${selectedProject.projectName} Smart Directory`}
              url={selectedProjectOrganisation.organisationLogo}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <StyledTextField
                select
                id="social-procurement-type"
                name="country"
                margin="dense"
                value={selectedTargetType}
                defaultValue={"ALL"}
                onChange={(s) => {
                  setSelectedTargetType(
                    s.target.value as SocialBusinessSearchCategory
                  );
                }}
                label="Social Target Type"
              >
                {socialTargets.map((c) => (
                  <MenuItem key={c.value} value={c.value}>
                    {c.label}
                  </MenuItem>
                ))}
              </StyledTextField>
            </Box>
          </Grid>
        </Grid>
      </StyledCard>

      <Grid container spacing={2} sx={{ display: "flex" }}>
        {!isSocialBusinessLoading &&
          socialBusinesses.map((c) => (
            <Grid
              item
              alignSelf={"stretch"}
              xs={4}
              md={4}
              sx={{ justifyContent: "stretch", justifyItems: "stretch" }}
            >
              <DirectoryCard
                name={c.socialBusinessName}
                logo={c.socialBusinessLogo}
                descriptions={c.socialBusinessBlurb}
                benefits={c.socialBusinessBeneficiaries}
                url={c.socialBusinessWebsite}
              />
            </Grid>
          ))}
      </Grid>
      {!isSocialBusinessLoading && socialBusinesses.length === 0 ? (
        <StyledCard>
          <Grid container spacing={2} sx={{ padding: "24px" }}>
            <Grid item xs={2} md={12}>
              <Table>
                <TableRow key={"no-data-row-0"}>
                  <TableCell colSpan={4} align="center"></TableCell>
                </TableRow>
                <TableRow key={"no-data-row-1"}>
                  <TableCell colSpan={4} align="center">
                    <Typography variant={"h4"} component="div" mt={1}>
                      No companies found
                    </Typography>
                  </TableCell>
                </TableRow>
                <TableRow key={"no-data-row-2"}>
                  <TableCell colSpan={4} align="center"></TableCell>
                </TableRow>
              </Table>
            </Grid>
          </Grid>
        </StyledCard>
      ) : null}
      {isSocialBusinessLoading ? (
        <StyledCard>
          <Grid container spacing={2} sx={{ padding: "24px" }}>
            <Grid item xs={2} md={12}>
              {isSocialBusinessLoading ? <Loader /> : null}{" "}
            </Grid>
          </Grid>
        </StyledCard>
      ) : null}
    </>
  );
};
